import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Extracts the section ID from a section element
 * @param {HTMLElement} element - The element to extract the section ID from
 * @returns {string} The section ID without the 'shopify-section-' prefix
 */
function extractSectionId(element) {
  const section = element.classList.contains('shopify-section') 
    ? element 
    : element.closest('.shopify-section');
  if (!section) return '';
  return section.id.replace('shopify-section-', '');
}

class RecentlyViewedProductsSection extends HTMLElement {
  /**
   * The observer for the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-products-loaded attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-products-loaded' &&
        this.dataset.productsLoaded === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadProducts();
      break;
    }
  });

  /**
   * The cached responses
   * @type {Record<string, string>}
   */
  #cachedResponses = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the recently viewed products
   */
  #loadProducts() {
    const { sectionId, excludeId, productsCount } = this.dataset;
    const id = this.id;

    if (!id) {
      throw new Error('An ID attribute is required');
    }

    // If the products have already been loaded
    if (this.dataset.productsLoaded === 'true') {
      return;
    }

    // Get products from localStorage
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) {
      this.#handleNoProducts();
      return;
    }

    // Exclude current product if on product page
    let filteredProducts = viewedProducts;
    if (excludeId) {
      filteredProducts = viewedProducts.filter(id => id !== excludeId);
    }

    // Limit products
    const maxProducts = productsCount ? parseInt(productsCount, 10) : 4;
    filteredProducts = filteredProducts.slice(0, maxProducts);

    if (filteredProducts.length === 0) {
      this.#handleNoProducts();
      return;
    }

    this.#fetchProducts(filteredProducts, sectionId)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        
        // Try to find the custom element directly
        let section = html.querySelector(`recently-viewed-products-section[id="${id}"]`);
        
        // If not found, try to find it within a shopify-section wrapper
        if (!section) {
          const shopifySection = html.querySelector('.shopify-section');
          if (shopifySection) {
            section = shopifySection.querySelector(`recently-viewed-products-section[id="${id}"]`);
          }
        }
        
        // If still not found, try any recently-viewed-products-section
        if (!section) {
          section = html.querySelector('recently-viewed-products-section');
        }

        if (section?.innerHTML && section.innerHTML.trim().length) {
          this.dataset.productsLoaded = 'true';
          this.innerHTML = section.innerHTML;
        } else {
          this.#handleError(new Error('No products available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the products and cached the result for future use
   * @param {string[]} productIds
   * @param {string | undefined} sectionId
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchProducts(productIds, sectionId) {
    // Build search query
    const searchQuery = productIds.map(id => `id:${id}`).join(' OR ');
    
    // Try using sectionRenderer first to preserve manual block configurations
    if (sectionId && window.Shopify?.sectionRenderer) {
      try {
        const sectionHTML = await window.Shopify.sectionRenderer.getSectionHTML(sectionId, {
          'resources[type]': 'product',
          'q': searchQuery
        });
        if (sectionHTML) {
          return { success: true, data: sectionHTML };
        }
      } catch (e) {
        console.warn('Recently viewed: sectionRenderer failed, falling back to fetch', e);
      }
    }
    
    // Fallback to direct fetch
    const searchUrl = new URL(Theme.routes.search_url, window.location.origin);
    searchUrl.searchParams.set('q', searchQuery);
    searchUrl.searchParams.set('resources[type]', 'product');
    if (sectionId) {
      searchUrl.searchParams.set('section_id', sectionId);
    }
    const url = searchUrl.toString();

    const cachedResponse = this.#cachedResponses[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { 
        signal: this.#activeFetch.signal,
        priority: 'low'
      });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedResponses[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle the case when there are no products
   */
  #handleNoProducts() {
    this.classList.add('hidden');
    this.dataset.error = 'No recently viewed products';
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products-section')) {
  customElements.define('recently-viewed-products-section', RecentlyViewedProductsSection);
}
